// Kundenliste: wer wichtig ist, wer schuldet, wer lange nicht gekauft hat.
// Ein Klick auf den Kunden öffnet seine Akte.
import { useMemo, useState } from 'react';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { Link, useNavigate } from 'react-router-dom';
import { useDaten, euro, datum } from '../../../gemeinsam/lib/daten';
import type { Kunde } from '../../../gemeinsam/lib/typen';
import { Abzeichen, Auswahl, Feld, Hinweis, Karte, Knopf, LeerZustand, Spinner } from '../../../gemeinsam/komponenten/Basis';
import KundenFormular, { LEERER_KUNDE, type KundenFelder } from '../komponenten/KundenFormular';
import { useSprache } from '../../../gemeinsam/lib/i18n';

type Sortierung = 'name' | 'umsatz' | 'offen' | 'zuletzt';
const SPEICHER = 'lp_kunden_sortierung';

export default function Kunden() {
  const { t } = useSprache();
  const navigate = useNavigate();
  const [suche, setSuche] = useState('');
  const { daten, laedt, fehler, neuLaden } = useDaten<Kunde[]>(
    `/customers${suche ? `?suche=${encodeURIComponent(suche)}` : ''}`, [suche]);

  const [formular, setFormular] = useState<KundenFelder | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [sortierung, setSortierungRoh] = useState<Sortierung>(() => {
    try { return (localStorage.getItem(SPEICHER) as Sortierung) || 'name'; } catch { return 'name'; }
  });
  const setSortierung = (s: Sortierung) => {
    setSortierungRoh(s);
    try { localStorage.setItem(SPEICHER, s); } catch { /* egal */ }
  };

  const liste = useMemo(() => {
    const l = [...(daten ?? [])];
    const z = (k: Kunde) => k.kennzahlen;
    if (sortierung === 'umsatz') l.sort((a, b) => (z(b)?.umsatzJahr ?? 0) - (z(a)?.umsatzJahr ?? 0));
    if (sortierung === 'offen') l.sort((a, b) => (z(b)?.offen ?? 0) - (z(a)?.offen ?? 0));
    if (sortierung === 'zuletzt') l.sort((a, b) => String(z(b)?.letzterKauf ?? '').localeCompare(String(z(a)?.letzterKauf ?? '')));
    return l;
  }, [daten, sortierung]);

  const jahr = new Date().getFullYear();

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('kunden.titel')}</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {daten ? t(daten.length === 1 ? 'kunden.einKunde' : 'kunden.anzahl').replace('{n}', String(daten.length)) : '…'}
          </p>
        </div>
        {!formular && (
          <Knopf variante="primaer" onClick={() => { setFormular({ ...LEERER_KUNDE }); setMeldung(null); }}>
            {t('kunden.neu')}
          </Knopf>
        )}
      </div>

      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      <Erfolgsmeldung text={meldung} />

      {formular && (
        <KundenFormular start={formular} beiAbbruch={() => setFormular(null)}
                        beiFertig={(k, neu) => {
                          setFormular(null);
                          if (neu) navigate(`/kunden/${k.id}`);
                          else { setMeldung(t('kunden.gespeichert')); void neuLaden(); }
                        }} />
      )}

      <Karte>
        <div className="flex flex-wrap items-end gap-3 border-b border-slate-200 p-4 dark:border-slate-800">
          <div className="min-w-[220px] flex-1">
            <Feld label={t('allg.suche')} value={suche} placeholder={t('kunden.suche')} onChange={e => setSuche(e.target.value)} />
          </div>
          <div className="w-48">
            <Auswahl label={t('kunden.sortieren')} value={sortierung} onChange={e => setSortierung(e.target.value as Sortierung)}>
              <option value="name">{t('feld.name')}</option>
              <option value="umsatz">{t('kunden.umsatzJahr').replace('{jahr}', String(jahr))}</option>
              <option value="offen">{t('kunden.offen')}</option>
              <option value="zuletzt">{t('kunden.letzterKaufSort')}</option>
            </Auswahl>
          </div>
        </div>

        {laedt && !daten ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : !liste.length ? (
          <LeerZustand titel={suche ? t('allg.keineTreffer') : t('kunden.leer')}
                       text={suche ? t('allg.andereSuche') : t('kunden.leerText')} />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            <li aria-hidden className="hidden px-5 py-2 text-xs uppercase tracking-wide text-slate-500 sm:flex dark:text-slate-400">
              <span className="flex-1">{t('feld.kunde')}</span>
              <span>{t('kunden.umsatzJahr').replace('{jahr}', String(jahr))}</span>
            </li>
            {liste.map(k => {
              const z = k.kennzahlen;
              return (
                <li key={k.id}>
                  <Link to={`/kunden/${k.id}`} className="group flex items-start gap-3 px-5 py-3.5 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-slate-900 group-hover:text-marke-700 dark:text-slate-100 dark:group-hover:text-marke-300">
                        {k.name}
                        {k.kundennummer && <span className="ms-2 text-xs font-normal text-slate-500 dark:text-slate-400">#{k.kundennummer}</span>}
                      </p>
                      <p className="truncate text-sm text-slate-500 dark:text-slate-400">
                        {[k.email, k.telefon, [k.plz, k.ort].filter(Boolean).join(' ')].filter(Boolean).join(' · ') || t('kunden.keineKontakt')}
                      </p>
                      {(!!z?.offen || !!k.guthaben) && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {!!z?.offen && (
                            <Abzeichen art={z.ueberfaellig ? 'rot' : 'gelb'}>
                              {t(z.ueberfaellig ? 'kunden.ueberfaelligKurz' : 'kunden.offenBetrag').replace('{betrag}', euro(z.ueberfaellig || z.offen))}
                            </Abzeichen>
                          )}
                          {!!k.guthaben && <Abzeichen art={k.guthaben > 0 ? 'gruen' : 'rot'}>{t('kunden.guthaben').replace('{betrag}', euro(k.guthaben))}</Abzeichen>}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">{euro(z?.umsatzJahr ?? 0)}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {z?.letzterKauf ? t('kunden.zuletzt').replace('{datum}', datum(z.letzterKauf)) : t('kunden.nochNichts')}
                      </p>
                    </div>
                    <span aria-hidden className="self-center text-slate-300 group-hover:text-marke-500 dark:text-slate-600">›</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </Karte>
    </div>
  );
}
